import { useState, type ReactElement } from 'react'
import type { Demand, DemandSource } from '@/types/knowledge_graph'
import { PencilIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Card } from '@/components/ui/card'
import { InferenceTypeBadge } from './InferenceTypeBadge'
import type { InferenceTypeMeta } from './InferenceTypeBadge'

function demandSourceTag (source: DemandSource): InferenceTypeMeta {
  if (source === 'implicit') {
    return { icon: '💭', label: '推論で補完', style: { background: 'var(--bp-accent-pale)', color: 'var(--bp-ai)' } }
  }
  return { icon: '📝', label: 'メモに明示', style: { background: 'var(--bp-surface2)', color: 'var(--bp-ink3)' } }
}

export function DemandCard ({
  demand,
  onUpdate
}: {
  demand: Demand
  onUpdate: (changes: Partial<Demand>) => void
}): ReactElement {
  const [editing, setEditing] = useState(false)
  const [draftName, setDraftName] = useState('')
  const [draftDescription, setDraftDescription] = useState('')
  const isApproved = demand.review_status === 'approved'

  function startEdit (): void {
    setDraftName(demand.name)
    setDraftDescription(demand.description)
    setEditing(true)
  }

  function commitEdit (): void {
    const trimmedName = draftName.trim()
    if (trimmedName === '') return
    const changes: Partial<Demand> = {}
    if (trimmedName !== demand.name) changes.name = trimmedName
    const trimmedDesc = draftDescription.trim()
    if (trimmedDesc !== demand.description) changes.description = trimmedDesc
    if (Object.keys(changes).length > 0) onUpdate(changes)
    setEditing(false)
  }

  function cancelEdit (): void { setEditing(false) }

  if (editing) {
    return (
      <Card
        className={cn(
          'relative border-[1.5px] rounded-lg shadow-none p-3.5',
          isApproved && 'border-(--bp-ok-border) bg-(--bp-ok-pale)'
        )}
      >
        <input
          autoFocus
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commitEdit() }
            if (e.key === 'Escape') cancelEdit()
          }}
          placeholder='要求名'
          className='w-full text-sm font-semibold border border-(--bp-accent) rounded px-1.5 py-0.75 bg-(--bp-surface) text-(--bp-ink) outline-none'
        />
        <textarea
          value={draftDescription}
          onChange={(e) => setDraftDescription(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') cancelEdit() }}
          placeholder='説明'
          rows={3}
          className='w-full mt-1 resize-y text-xs border border-(--bp-border) rounded px-1.5 py-0.75 bg-(--bp-surface) text-(--bp-ink2) outline-none'
        />
        <div className='flex gap-1 justify-end mt-1.5'>
          <button
            onClick={cancelEdit}
            className='text-[11px] px-2 py-0.5 border border-(--bp-border) rounded bg-(--bp-surface2) text-(--bp-ink2) cursor-pointer'
          >
            キャンセル
          </button>
          <button
            onClick={commitEdit}
            className='text-[11px] px-2 py-0.5 border-none rounded bg-(--bp-accent) text-white cursor-pointer'
          >
            保存
          </button>
        </div>
      </Card>
    )
  }

  const isInferred = demand.source === 'implicit'

  return (
    <Card
      className={cn(
        'relative border-[1.5px] rounded-lg shadow-none p-3.5 pt-2',
        isApproved
          ? 'border-(--bp-ok-border) bg-(--bp-ok-pale)'
          : isInferred && 'border-l-3 border-l-(--bp-warning) bg-(--bp-warning-pale)'
      )}
    >
      <div className='flex items-center justify-between mb-1'>
        <label className='flex items-center gap-1.75 cursor-pointer'>
          <input
            type='checkbox'
            checked={isApproved}
            className='size-3.5 accent-(--bp-ok) cursor-pointer'
            onChange={() => onUpdate({ review_status: isApproved ? 'pending' : 'approved' })}
          />
          <span className={cn('text-[11px] font-bold tracking-[.04em]', isApproved ? 'text-(--bp-ok)' : 'text-(--bp-ink3)')}>
            {isApproved ? '確認済み' : '未確認'}
          </span>
        </label>
        {demand.source != null && (
          <div className='mr-6 shrink-0'>
            <InferenceTypeBadge tag={demandSourceTag(demand.source)} reasoning={demand.reasoning} />
          </div>
        )}
      </div>
      <div className='text-[11px] font-mono text-(--bp-ink3) mb-0.75'>{demand.id}</div>
      <div className='text-sm font-bold text-(--bp-ink) mb-1.25 tracking-tight'>{demand.name}</div>
      <div className='text-xs text-(--bp-ink2) leading-relaxed'>{demand.description}</div>
      <button
        className='absolute top-2 right-2 size-6 flex items-center justify-center bg-(--bp-surface2) border border-(--bp-border) rounded-[5px] cursor-pointer text-(--bp-ink3) p-0 transition-colors hover:bg-(--bp-accent-pale) hover:text-(--bp-accent) hover:border-[rgba(53,71,193,.3)]'
        title='要求を修正'
        onClick={startEdit}
      >
        <PencilIcon width={12} height={12} />
      </button>
    </Card>
  )
}
